/**
 * Hero images for the `hazardous-waste` Pitch variant, where the right half of
 * the hero section is a full-bleed photo.
 *
 * Entries are keyed by *image*, not by page: the pages sharing a photo also
 * share the surrounding layout, so one photo needs exactly one crop. A page
 * opts in by passing the image key as `heroImage` to <Pitch>; pages that pass
 * nothing get `hazardous-waste`, which is the framing the variant was built
 * around. Values become CSS custom properties consumed by
 * `.mainSectionHazardousWaste::after` in src/styles/disposal/pitch.module.css.
 *
 * Tuning cheatsheet (both values are plain CSS, applied to a box covering the
 * right 52% of the hero; the image is hidden entirely below 991px):
 *
 *   size      'cover'      fill the box, crop the overflow
 *             'auto 150%'  scale to 150% of the box height, crop the sides;
 *                          raise the % to zoom in, lower it to zoom out
 *             'contain'    fit the whole photo in, letterboxing the rest
 *   position  'center'     centred both ways
 *             '50% 30%'    x% y% — smaller y pans the crop toward the top
 *             '40px 70px'  x y offsets from the box's top-left corner
 *
 * A percentage `size` resolves against the hero's own height, and the hero is
 * as tall as its content, so a page with unusually short or long intro copy
 * renders a slightly tighter or looser crop than its neighbours. Correct that
 * one page with the `heroImageOverride` prop rather than by moving the shared
 * value here.
 */

/** All three are required: the stylesheet carries no fallbacks of its own. */
export type PitchHeroImage = {
  /** Absolute path under /public. */
  url: string
  /** `background-size`, e.g. 'cover' or 'auto 150%'. */
  size: string
  /** `background-position`, e.g. 'center' or '40px 70px'. */
  position: string
}

/**
 * Per-page correction merged over the shared entry — for a page whose hero
 * height is off from the rest of its group. Keep these rare; the shared values
 * above are what should be doing the work.
 */
export type PitchHeroImageOverride = Partial<Omit<PitchHeroImage, 'url'>>

export const pitchHeroImages = {
  /** Default for the variant — every hazardous waste / paint / lab pack page. */
  'hazardous-waste': {
    url: '/images/paint-disposal/paint-disposal-landing.jpg',
    size: 'auto 110%',
    position: '40px 70px',
  },

  /**
   * The hazardous waste hub. The React app's own hazardous landing illustration
   * (`assets/landing_page/haz-waste-landing-v6.jpg` — a technician loading
   * colour-coded drums onto a box truck), re-hosted. Source is 1080x998, near
   * enough square to take the same crop as `beverage`. Only the hub uses it;
   * the leaf pages stay on the shared `hazardous-waste` photo above.
   */
  'hazardous-drums': {
    url: '/images/hazardous-waste-disposal/hazardous-waste-landing.jpg',
    size: 'auto 100%',
    position: '40px 70px',
  },

  /** Beverage and alcohol services. Source photo is 1024x1024. */
  beverage: {
    url: '/images/beverage-disposal/beverage-disposal-landing.jpg',
    size: 'auto 100%',
    position: '40px 70px',
  },

  /** Hand sanitizer services. Source photo is 2048x1365 (landscape). */
  'hand-sanitizer': {
    url: '/images/sanitizer-disposal/hand-sanitizer-landing.jpg',
    size: 'auto 120%',
    position: '40px 70px',
  },

  /** Junk removal plus the adjacent hauling / container services. Source photo is 896x512. */
  junk: {
    url: '/images/junk-removal/junk-removal-landing.png',
    size: 'auto 80%',
    position: '40px 70px',
  },

  /**
   * Commercial composting pages. The four-colour wheelie bin line-up the React
   * app ran behind the compost quote form (`assets/landing/pawel-czerwinski-
   * RkIsyD_AVvc-unsplash.jpg` on the prod bucket), re-hosted. The 5697x3798
   * landscape source is centre-cropped to 900x1200 — the hero box's own
   * portrait — so 'cover' fills it while keeping three whole bins in frame.
   */
  compost: {
    url: '/images/commercial-composting/compost-cta-background.jpg',
    size: 'cover',
    position: '40px 70px',
  },

  /**
   * Solar panel disposal, decommissioning, and sell/buy-back pages. The Dimension
   * solar illustration (sun over a field, panels being loaded onto a truck).
   * Source is 1600x914 (landscape); 'cover' keeps the full vertical scene and
   * crops the outer panels in the right-hand hero box.
   */
  solar: {
    url: '/images/solar-panel-disposal/solar-landing.jpg',
    size: 'auto 110%',
    position: '40px 70px',
  },

  /**
   * Fire debris removal. The Dimension fire cleanup illustration
   * (`assets/landing/fire_debris_pitch.webp` on the prod bucket), re-hosted.
   * Source is 1024x1024, the same square framing as `beverage`, so it takes the
   * same crop.
   */
  'fire-debris': {
    url: '/images/fire-debris-removal/fire-debris-landing.webp',
    size: 'auto 100%',
    position: '40px 70px',
  },
} as const satisfies Record<string, PitchHeroImage>

export type PitchHeroImageKey = keyof typeof pitchHeroImages

export const defaultPitchHeroImage: PitchHeroImageKey = 'hazardous-waste'

export default pitchHeroImages
